import { ConfirmationModalBody } from '@/components/ConfirmationModal/Base/Body';
import { useConfirm } from '@/components/ConfirmationModal/Base/hooks';
import styles from '@/components/ConfirmationModal/Base/Modal.module.scss';
import { useTranslation } from '@/i18n';
import { useGlobalContainerRef } from '@/wc/helper/ref';
import {
  ButtonGroup,
  ButtonV2,
  Checkbox,
  Modal,
} from '@moneyforward/ap-frontend-components';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import classnames from 'classnames/bind';
import parser from 'html-react-parser';
import { FC, memo, useCallback, useMemo, useState } from 'react';

const cx = classnames.bind(styles);

type EnableConfirmModalContentProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  containerRef: () => HTMLElement | ShadowRoot;
  year: number;
  month: number;
};

const EnableConfirmModalContent: FC<EnableConfirmModalContentProps> = memo(
  ({ isOpen, onClose, onConfirm, containerRef, year, month }) => {
    const { t } = useTranslation();
    const [isAgreed, setIsAgreed] = useState(false);

    const handleClose = useCallback(() => {
      setIsAgreed(false);
      onClose();
    }, [onClose]);

    const handleConfirm = useCallback(() => {
      setIsAgreed(false);
      onConfirm();
    }, [onConfirm]);

    const handleCheckboxChange = useCallback((e: CheckboxChangeEvent) => {
      setIsAgreed(e.target.checked);
    }, []);

    return (
      <Modal
        open={isOpen}
        onClose={handleClose}
        isOutsideClose
        isEscKeyClose
        getContainer={containerRef}
        title={t('business_document_setting_confirm_title')}
        size='md'
        footer={
          <ButtonGroup>
            <ButtonV2
              color='primary'
              isSecondary
              size='sm'
              onClick={handleClose}
            >
              {t('cancel')}
            </ButtonV2>
            <ButtonV2
              color='primary'
              size='sm'
              onClick={handleConfirm}
              isDisabled={!isAgreed}
            >
              {t('business_document_setting_confirm_enable_button')}
            </ButtonV2>
          </ButtonGroup>
        }
        modalWrapperClassName={cx(styles['container'])}
      >
        <ConfirmationModalBody>
          <div className='stack-8'>
            {parser(
              t('business_document_setting_confirm_enable_body', {
                year: year.toString(),
                month: month.toString(),
              })
            )}
            <Checkbox
              checked={isAgreed}
              onChange={handleCheckboxChange}
              label={t('business_document_setting_confirm_enable_agreement')}
            />
          </div>
        </ConfirmationModalBody>
      </Modal>
    );
  }
);
EnableConfirmModalContent.displayName = 'EnableConfirmModalContent';

export const useEnableConfirmModal = () => {
  const containerRef = useGlobalContainerRef();
  const { isOpen, confirm, handleSuccess, handleCancel } = useConfirm();

  const { year, month } = useMemo(() => {
    const now = new Date();
    return {
      year: now.getFullYear(),
      month: now.getMonth() + 1,
    };
  }, []);

  const ConfirmModal: FC = useCallback(
    () => (
      <EnableConfirmModalContent
        isOpen={isOpen}
        onClose={handleCancel}
        onConfirm={handleSuccess}
        containerRef={containerRef}
        year={year}
        month={month}
      />
    ),
    [containerRef, handleCancel, handleSuccess, isOpen, month, year]
  );

  return useMemo(
    () => ({
      confirm,
      ConfirmModal,
    }),
    [ConfirmModal, confirm]
  );
};
